// NOTE: This type should have the same values as the enum described in Prisma for the type Role
export const ROLES = {
  PERSONAL: 'personal',
  CLINIC: 'clinic',
  MAINTENANCE: 'maintenance',
  MARSI: 'marsi',
  TESTER: 'tester',
  COMMERCIAL: 'commercial',
  DISTRIBUTOR: 'distributor',
  SPECIALIST_CLINIC: 'specialist_clinic',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

// Definition of available resources (this is very similar to the available
// pages and available resources in Marsinet)
export type Resource =
  | 'devices'
  | 'organizations'
  | 'ingestions'
  | 'sessions'
  | 'patients'
  | 'incidences'
  | 'dashboards'
  | 'users'
  | 'cache'
  | 'health'
  | 'ota'
  | 'visualization'
  | 'surveys'
  | 'videos'
  | 'required_versions';

// Definition of all available actions (some of them are generic, some of them
// have been created specifically for a resource)
type GenericAction = 'read' | 'create' | 'delete' | 'update' | 'read:section';

type DeviceAction =
  | 'read:details_section'
  | 'read:alarms'
  | 'view:devices_page'
  | 'view:devices_detail_page'
  | 'update:state';

type OrganizationAction =
  | 'read:organizations_control_panel'
  | 'update:organizations_premium'
  | 'update:organizations_block'
  | 'view:institutions_page'
  | 'read:alarms'
  | 'update:organizations_add_device'
  | 'update:organizations_del_device'
  | 'update:organizations_creator'
  | 'create:marsi_organizations'
  | 'read:org_name'
  | 'read:org_role';

type IngestionAction =
  | 'read:ingestions_download_zips'
  | 'read:ingestions_upload_zips'
  | 'read:ingestions_file_name'
  | 'read:ingestions_processed_json'
  | 'delete:ingestions_blobs';

type PatientAction =
  | 'read:patients_identifier'
  | 'update:patients_personal_data'
  | 'update:patients_config'
  | 'update:patients_add_to_org'
  | 'update:patients_del_from_org'
  | 'update:patients_block_status'
  | 'update:patients_config_history'
  | 'view:edit_patient_page';

type UserAction =
  | 'update:users_active'
  | 'update:users_is_admin'
  | 'update:users_block'
  | 'update:users_unlock'
  | 'update:users_reset_password'
  | 'assign_marsi_role' // This action allows to view the option to assign the Marsi role to a user, and to perform the assignment
  | 'can_activate_pending_manually' // This action allows to view the option to activate pending users manually, and to perform the activation
  | 'read:organizations_available_creators'
  | 'delete:pending_users'; // This action allows to delete users that are in pending state (not activated yet only ADMIN MARSI)

type VisualActions =
  | 'view_delete_owner' // This action is used for dashboards, it allows to view and delete the dashboard if the user is the owner, but not to update it
  | 'view_read_dashboard' // This action is used for dashboards, it allows to view and edit the dashboard if the user is the owner
  | 'view_assistance_modes_graphs' // This action controls visibility of AAN assistance modes in charts
  | 'view_steps_ranking_graphs' // This action is used for dashboards, it allows to view the stepsByMode graphs (that are only visible for clinic and maintenance)
  | 'view_rom_labels'  // This action controls visibility of ROM labels in charts
  | 'view_patient_history'; // This action controls visibility of patient history in charts

type SurveyActions = 'read:pending_section';

type VideoActions = 'update:manage_tags' | 'read:download';

type SessionAction = 'read:main_panel_sessions';

export type Action =
  | GenericAction
  | DeviceAction
  | OrganizationAction
  | IngestionAction
  | PatientAction
  | UserAction
  | VisualActions
  | SurveyActions
  | VideoActions
  | SessionAction;

// Definition of all available Scopes (detail actions scope)
export type Scope = 'all' | 'all_non_admin' | 'org' | 'child_orgs';

// Definition of Available Actions for the different Resources
export type Permission = `${Resource}:${Action}`;

export const AvailableResourceActions = {
  devices: {
    read_base: 'devices:read',
    read_section: 'devices:read:section',
    read_details_section: 'devices:read:details_section',
    read_alarms: 'devices:read:alarms',
    view_page: 'devices:view:devices_page', // TODO: check if this action is really necessary, its used only in the front and I think read_section already behaves the same
    view_detail_page: 'devices:view:devices_detail_page', // TODO: check if this action is really necessary
    create: 'devices:create',
    delete: 'devices:delete',
    update_base: 'devices:update',
    update_state: 'devices:update:state',
  },
  organizations: {
    view_page: 'organizations:view:institutions_page',
    read_base: 'organizations:read',
    read_section: 'organizations:read:section',
    read_control_panel: 'organizations:read:organizations_control_panel',
    create: 'organizations:create',
    delete: 'organizations:delete',
    update_base: 'organizations:update',
    update_premium: 'organizations:update:organizations_premium',
    update_block: 'organizations:update:organizations_block',
    update_creator: 'organizations:update:organizations_creator',
    update_add_devices: 'organizations:update:organizations_add_device',
    update_del_devices: 'organizations:update:organizations_del_device',
    create_marsi_organizations: 'organizations:create:marsi_organizations',
    read_org_name: 'organizations:read:org_name',
    read_org_role: 'organizations:read:org_role',
  },
  ingestions: {
    read_base: 'ingestions:read',
    read_section: 'ingestions:read:section',
    read_file_names: 'ingestions:read:ingestions_file_name',
    read_processed_json: 'ingestions:read:ingestions_processed_json',
    download_zips: 'ingestions:read:ingestions_download_zips',
    upload_zips: 'ingestions:read:ingestions_upload_zips',
    delete_blobs: 'ingestions:delete:ingestions_blobs',
    delete: 'ingestions:delete',
  },
  sessions: {
    read_base: 'sessions:read',
    read_section: 'sessions:read:section',
    read_main_panel: 'sessions:read:main_panel_sessions',
  },
  patients: {
    read_base: 'patients:read',
    read_identifier: 'patients:read:patients_identifier',
    create: 'patients:create',
    delete: 'patients:delete',
    update_base: 'patients:update',
    update_personal_data: 'patients:update:patients_personal_data',
    update_config: 'patients:update:patients_config',
    update_add_to_org: 'patients:update:patients_add_to_org',
    update_del_from_org: 'patients:update:patients_del_from_org',
    update_block_status: 'patients:update:patients_block_status',
    update_config_history: 'patients:update:patients_config_history'
  },
  incidences: {
    read_section: 'incidences:read:section',
  },
  dashboards: {
    read_base: 'dashboards:read',
    read_section: 'dashboards:read:section',
    create: 'dashboards:create',
    delete: 'dashboards:delete',
    update_base: 'dashboards:update',
  },
  users: {
    read_base: 'users:read',
    read_section: 'users:read:section',
    create: 'users:create',
    delete: 'users:delete',
    update_base: 'users:update',
    update_is_admin: 'users:update:users_is_admin',
    update_block: 'users:update:users_block',
    update_active: 'users:update:users_active',
    update_unlock: 'users:update:users_unlock',
    update_reset_password: 'users:update:users_reset_password',
    assign_marsi_role: 'users:assign_marsi_role',
    can_activate_pending_manually: 'users:can_activate_pending_manually',
    read_available_creators: 'users:read:organizations_available_creators',
    delete_pending_users: 'users:delete:pending_users',
  },
  cache: {
    read_base: 'cache:read',
    update_base: 'cache:update',
  },
  health: {
    read_base: 'health:read',
  },
  ota: {
    create: 'ota:create',
  },
  visualization: {
    view_delete_owner: 'visualization:view_delete_owner',
    view_read_dashboard: 'visualization:view_read_dashboard',
    view_assistance_modes_graphs: 'visualization:view_assistance_modes_graphs',
    view_steps_ranking_graphs: 'visualization:view_steps_ranking_graphs',
    view_rom_labels: 'visualization:view_rom_labels',
    view_patient_history: 'visualization:view_patient_history',
  },
  surveys: {
    read_section: 'surveys:read:section', //Este permiso permite acceder a la sección de Encuestas (de Creación) por lo que este permiso permite todas las acciones disponibles en esa sección. (tambien borrar ).
    read_pending_section: 'surveys:read:pending_section', //Permiso pensado para usuarios únicamente que responden encuestas.
  },
  videos: {
    create: 'videos:create',
    read: 'videos:read',
    delete: 'videos:delete',
    manage_tags: 'videos:update:manage_tags',
    download: 'videos:read:download',
  },
  required_versions: {
    update: 'required_versions:update'
  }
} as const satisfies Record<Resource, Record<string, Permission>>;

// Assign actions to roles
// NOTE: A Role is given permissions in the form of Rules: [resources + actions + scope + only_org_admin]
export type Rule = {
  perm: Permission | '*' | `${Resource}:*`;
  scope?: Scope;
  only_org_admin?: Boolean;
};

const ROLE_RULES: Record<Role, Rule[]> = {
  marsi: [
    // all permissions all scopes
    { perm: '*', scope: 'all' },
  ],
  personal: [
    // devices
    { perm: AvailableResourceActions.devices.read_base },

    // organizations
    { perm: AvailableResourceActions.organizations.read_base, scope: 'org' },
    {
      perm: AvailableResourceActions.organizations.update_base,
      scope: 'org',
      only_org_admin: true,
    },

    // ingestion
    { perm: AvailableResourceActions.ingestions.read_base, scope: 'org' },
    { perm: AvailableResourceActions.ingestions.upload_zips, scope: 'org' },
    { perm: AvailableResourceActions.ingestions.read_file_names, scope: 'org' },
    {
      perm: AvailableResourceActions.ingestions.read_processed_json,
      scope: 'org',
    },

    { perm: AvailableResourceActions.ingestions.read_base, scope: 'org' },
    { perm: AvailableResourceActions.ingestions.upload_zips, scope: 'org' },
    { perm: AvailableResourceActions.ingestions.read_file_names, scope: 'org' },
    {
      perm: AvailableResourceActions.ingestions.read_processed_json,
      scope: 'org',
    },

    // sessions
    { perm: AvailableResourceActions.sessions.read_base, scope: 'org' },
    { perm: AvailableResourceActions.sessions.read_section },
    { perm: AvailableResourceActions.sessions.read_main_panel, scope: 'org' },

    // patients
    { perm: AvailableResourceActions.patients.read_base },
    { perm: AvailableResourceActions.patients.update_base },
    { perm: AvailableResourceActions.patients.update_personal_data },
    { perm: AvailableResourceActions.patients.update_del_from_org },
    { perm: AvailableResourceActions.patients.update_block_status },

    // users
    { perm: AvailableResourceActions.users.read_base, scope: 'org' },
    { perm: AvailableResourceActions.users.read_section },
    {
      perm: AvailableResourceActions.users.update_is_admin,
      scope: 'org',
      only_org_admin: true,
    },
    {
      perm: AvailableResourceActions.users.update_active,
      scope: 'org',
      only_org_admin: true,
    },
    {
      perm: AvailableResourceActions.users.delete,
      scope: 'org',
      only_org_admin: true,
    },
    // surveys
    { perm: AvailableResourceActions.surveys.read_pending_section },
    // videos
    { perm: AvailableResourceActions.videos.read, scope: 'org' },
  ],
  clinic: [
    // devices
    { perm: AvailableResourceActions.devices.view_page },
    { perm: AvailableResourceActions.devices.view_detail_page },
    { perm: AvailableResourceActions.devices.read_base },
    { perm: AvailableResourceActions.devices.read_details_section },

    // organizations
    { perm: AvailableResourceActions.organizations.read_base, scope: 'org' },
    {
      perm: AvailableResourceActions.organizations.update_base,
      scope: 'org',
      only_org_admin: true,
    },

    //visualization
    { perm: AvailableResourceActions.visualization.view_rom_labels, scope: 'org' },
    { perm: AvailableResourceActions.visualization.view_patient_history, scope: 'org' },


    // ingestion
    { perm: AvailableResourceActions.ingestions.read_base, scope: 'org' },
    { perm: AvailableResourceActions.ingestions.upload_zips, scope: 'org' },
    { perm: AvailableResourceActions.ingestions.read_file_names, scope: 'org' },
    {
      perm: AvailableResourceActions.ingestions.read_processed_json,
      scope: 'org',
    },

    // sessions
    { perm: AvailableResourceActions.sessions.read_section },
    { perm: AvailableResourceActions.sessions.read_base, scope: 'org' },
    { perm: AvailableResourceActions.sessions.read_main_panel, scope: 'org' },

    // patients
    { perm: AvailableResourceActions.patients.read_base },
    { perm: AvailableResourceActions.patients.create },
    { perm: AvailableResourceActions.patients.delete },
    { perm: AvailableResourceActions.patients.update_base },
    { perm: AvailableResourceActions.patients.update_personal_data },
    { perm: AvailableResourceActions.patients.update_config },
    { perm: AvailableResourceActions.patients.update_add_to_org },
    { perm: AvailableResourceActions.patients.update_del_from_org },
    { perm: AvailableResourceActions.patients.update_block_status },
    { perm: AvailableResourceActions.patients.update_config_history },

    // users
    { perm: AvailableResourceActions.users.read_base, scope: 'org' },
    { perm: AvailableResourceActions.users.read_section },
    {
      perm: AvailableResourceActions.users.update_is_admin,
      scope: 'org',
      only_org_admin: true,
    },
    {
      perm: AvailableResourceActions.users.update_active,
      scope: 'org',
      only_org_admin: true,
    },
    // graphs
    {
      perm: AvailableResourceActions.visualization.view_assistance_modes_graphs,
      scope: 'org',
    },
    // surveys
    { perm: AvailableResourceActions.surveys.read_pending_section },
    {
      perm: AvailableResourceActions.visualization.view_steps_ranking_graphs,
      scope: 'org',
    },
    // videos
    { perm: AvailableResourceActions.videos.read, scope: 'org' },
  ],

  maintenance: [
    // devices
    { perm: AvailableResourceActions.devices.view_page },
    { perm: AvailableResourceActions.devices.view_detail_page },
    { perm: AvailableResourceActions.devices.read_base },
    { perm: AvailableResourceActions.devices.read_section },
    { perm: AvailableResourceActions.devices.read_details_section },
    { perm: AvailableResourceActions.devices.read_alarms },
    { perm: AvailableResourceActions.devices.create },
    { perm: AvailableResourceActions.devices.update_state },

    // organizations
    { perm: AvailableResourceActions.organizations.read_base, scope: 'org' },
    {
      perm: AvailableResourceActions.organizations.update_base,
      scope: 'org',
      only_org_admin: true,
    },

    // ingestion
    { perm: AvailableResourceActions.ingestions.read_base, scope: 'org' },
    { perm: AvailableResourceActions.ingestions.read_section },
    { perm: AvailableResourceActions.ingestions.read_file_names, scope: 'org' },
    {
      perm: AvailableResourceActions.ingestions.read_processed_json,
      scope: 'org',
    },
    { perm: AvailableResourceActions.ingestions.upload_zips, scope: 'org' },
    { perm: AvailableResourceActions.ingestions.download_zips, scope: 'org' },

    // sessions
    { perm: AvailableResourceActions.sessions.read_base, scope: 'org' },
    {
      perm: AvailableResourceActions.sessions.read_main_panel,
      scope: 'all_non_admin',
    },

    // patients
    { perm: AvailableResourceActions.patients.read_base },
    { perm: AvailableResourceActions.patients.create },
    { perm: AvailableResourceActions.patients.delete },
    { perm: AvailableResourceActions.patients.update_base },
    { perm: AvailableResourceActions.patients.update_personal_data },
    { perm: AvailableResourceActions.patients.update_config },

    // incidences
    { perm: AvailableResourceActions.incidences.read_section },

    // users
    { perm: AvailableResourceActions.users.read_base, scope: 'org' },
    { perm: AvailableResourceActions.users.read_section },
    {
      perm: AvailableResourceActions.users.update_is_admin,
      scope: 'org',
      only_org_admin: true,
    },
    {
      perm: AvailableResourceActions.users.update_active,
      scope: 'org',
      only_org_admin: true,
    },
    // graphs
    {
      perm: AvailableResourceActions.visualization.view_assistance_modes_graphs,
      scope: 'org',
    },
    // videos
    { perm: AvailableResourceActions.videos.read, scope: 'org' },
  ],

  tester: [
    // devices
    { perm: AvailableResourceActions.devices.read_base },

    // organizations
    { perm: AvailableResourceActions.organizations.read_base, scope: 'org' },
    {
      perm: AvailableResourceActions.organizations.update_base,
      scope: 'org',
      only_org_admin: true,
    },

    // ingestion
    { perm: AvailableResourceActions.ingestions.read_base, scope: 'org' },
    { perm: AvailableResourceActions.ingestions.upload_zips, scope: 'org' },
    { perm: AvailableResourceActions.ingestions.read_file_names, scope: 'org' },
    {
      perm: AvailableResourceActions.ingestions.read_processed_json,
      scope: 'org',
    },

    // sessions
    { perm: AvailableResourceActions.sessions.read_base, scope: 'org' },
    { perm: AvailableResourceActions.sessions.read_section },
    { perm: AvailableResourceActions.sessions.read_main_panel, scope: 'org' },

    // patients
    { perm: AvailableResourceActions.patients.read_base },
    { perm: AvailableResourceActions.patients.update_base },
    { perm: AvailableResourceActions.patients.update_personal_data },
    { perm: AvailableResourceActions.patients.update_del_from_org },
    { perm: AvailableResourceActions.patients.update_block_status },

    // users
    { perm: AvailableResourceActions.users.read_base, scope: 'org' },
    { perm: AvailableResourceActions.users.read_section },
    {
      perm: AvailableResourceActions.users.update_is_admin,
      scope: 'org',
      only_org_admin: true,
    },
    {
      perm: AvailableResourceActions.users.update_active,
      scope: 'org',
      only_org_admin: true,
    },
    // graphs
    {
      perm: AvailableResourceActions.visualization.view_assistance_modes_graphs,
      scope: 'org',
    },
    // surveys
    { perm: AvailableResourceActions.surveys.read_pending_section },
  ],

  commercial: [
    // devices
    { perm: 'devices:*' },

    // organizations
    { perm: 'organizations:*', scope: 'all_non_admin' },
    {
      perm: AvailableResourceActions.organizations.read_control_panel,
      scope: 'all',
    },
    {
      perm: AvailableResourceActions.organizations.read_org_role,
      scope: 'org',
    },

    // patients
    { perm: AvailableResourceActions.patients.read_base },
    { perm: AvailableResourceActions.patients.create },
    { perm: AvailableResourceActions.patients.delete },
    { perm: AvailableResourceActions.patients.update_base },
    { perm: AvailableResourceActions.patients.update_personal_data },
    { perm: AvailableResourceActions.patients.update_config },

    // incidences
    { perm: AvailableResourceActions.incidences.read_section },

    // dashboards
    {
      perm: AvailableResourceActions.visualization.view_assistance_modes_graphs,
      scope: 'all',
    },
    { perm: 'dashboards:*' },

    // sessions
    { perm: AvailableResourceActions.sessions.read_base, scope: 'all' },
    { perm: AvailableResourceActions.sessions.read_main_panel, scope: 'all' },

    // users
    { perm: AvailableResourceActions.users.read_base, scope: 'all_non_admin' },
    { perm: AvailableResourceActions.users.read_section },
    {
      perm: AvailableResourceActions.users.update_base,
      scope: 'all_non_admin',
      only_org_admin: false,
    },
    {
      perm: AvailableResourceActions.users.update_is_admin,
      scope: 'all_non_admin',
      only_org_admin: false,
    },
    {
      perm: AvailableResourceActions.users.update_block,
      scope: 'all_non_admin',
    },
    {
      perm: AvailableResourceActions.users.update_active,
      scope: 'all_non_admin',
      only_org_admin: false,
    },
    {
      perm: AvailableResourceActions.users.update_unlock,
      scope: 'all_non_admin',
    },
    {
      perm: AvailableResourceActions.users.read_available_creators,
      scope: 'all_non_admin',
    },

    // surveys
    { perm: AvailableResourceActions.surveys.read_section },
    { perm: AvailableResourceActions.surveys.read_pending_section },

    // Visualization
    {
      perm: AvailableResourceActions.visualization.view_steps_ranking_graphs,
      scope: 'org',
    },
    // videos
    { perm: AvailableResourceActions.videos.read, scope: 'all' },
    { perm: AvailableResourceActions.videos.manage_tags, scope: 'all' },
  ],

  distributor: [
    // devices
    { perm: AvailableResourceActions.devices.view_page },
    { perm: AvailableResourceActions.devices.view_detail_page },
    { perm: AvailableResourceActions.devices.read_base },
    { perm: AvailableResourceActions.devices.read_section },
    { perm: AvailableResourceActions.devices.read_details_section },
    { perm: AvailableResourceActions.devices.read_alarms },
    { perm: AvailableResourceActions.devices.update_state },

    // organizations
    {
      perm: AvailableResourceActions.organizations.view_page,
      scope: 'child_orgs',
    },
    {
      perm: AvailableResourceActions.organizations.read_base,
      scope: 'child_orgs',
    },
    { perm: AvailableResourceActions.organizations.read_section },
    {
      perm: AvailableResourceActions.organizations.read_control_panel,
      scope: 'child_orgs',
    },
    {
      perm: AvailableResourceActions.organizations.update_add_devices,
      scope: 'child_orgs',
    },
    {
      perm: AvailableResourceActions.organizations.update_del_devices,
      scope: 'child_orgs',
    },
    {
      perm: AvailableResourceActions.organizations.create,
      scope: 'child_orgs',
    },
    {
      perm: AvailableResourceActions.organizations.update_base,
      scope: 'child_orgs',
    },
    {
      perm: AvailableResourceActions.organizations.update_creator,
      scope: 'child_orgs',
      only_org_admin: true,
    },
    {
      perm: AvailableResourceActions.organizations.create,
      scope: 'child_orgs',
    },
    {
      perm: AvailableResourceActions.organizations.update_base,
      scope: 'child_orgs',
    },
    {
      perm: AvailableResourceActions.organizations.update_creator,
      scope: 'child_orgs',
      only_org_admin: true,
    },
    {
      perm: AvailableResourceActions.organizations.read_org_name,
      scope: 'child_orgs',
    },

    // ingestion
    { perm: AvailableResourceActions.ingestions.read_base, scope: 'org' },
    { perm: AvailableResourceActions.ingestions.read_section },
    { perm: AvailableResourceActions.ingestions.read_file_names, scope: 'org' },
    {
      perm: AvailableResourceActions.ingestions.read_processed_json,
      scope: 'org',
    },
    { perm: AvailableResourceActions.ingestions.upload_zips, scope: 'org' },
    { perm: AvailableResourceActions.ingestions.download_zips, scope: 'org' },

    // sessions
    { perm: AvailableResourceActions.sessions.read_base, scope: 'org' },
    { perm: AvailableResourceActions.sessions.read_section },
    {
      perm: AvailableResourceActions.sessions.read_main_panel,
      scope: 'child_orgs',
    },

    // patients
    { perm: AvailableResourceActions.patients.read_base },
    { perm: AvailableResourceActions.patients.create },
    { perm: AvailableResourceActions.patients.delete },
    { perm: AvailableResourceActions.patients.update_base },
    { perm: AvailableResourceActions.patients.update_personal_data },
    { perm: AvailableResourceActions.patients.update_config },
    { perm: AvailableResourceActions.patients.update_add_to_org },
    { perm: AvailableResourceActions.patients.update_del_from_org },
    { perm: AvailableResourceActions.patients.update_block_status },
    { perm: AvailableResourceActions.patients.update_config_history },

    // incidences
    { perm: AvailableResourceActions.incidences.read_section },

    // users
    { perm: AvailableResourceActions.users.read_base, scope: 'child_orgs' },
    { perm: AvailableResourceActions.users.read_section, scope: 'child_orgs' },
    { perm: AvailableResourceActions.users.create, scope: 'child_orgs' },
    { perm: AvailableResourceActions.users.delete, scope: 'child_orgs' },
    { perm: AvailableResourceActions.users.update_base, scope: 'child_orgs' },
    {
      perm: AvailableResourceActions.users.update_is_admin,
      scope: 'child_orgs',
    },
    { perm: AvailableResourceActions.users.update_block, scope: 'child_orgs' },
    { perm: AvailableResourceActions.users.update_active, scope: 'child_orgs' },
    { perm: AvailableResourceActions.users.update_unlock, scope: 'child_orgs' },
    {
      perm: AvailableResourceActions.users.read_available_creators,
      scope: 'org',
    },

    // graphs
    {
      perm: AvailableResourceActions.visualization.view_assistance_modes_graphs,
      scope: 'org',
    },
    // visualizations
    {
      perm: AvailableResourceActions.visualization.view_steps_ranking_graphs,
      scope: 'org',
    },
    { perm: AvailableResourceActions.visualization.view_patient_history, scope: 'org' },

    // videos
    { perm: AvailableResourceActions.videos.read, scope: 'org' },
  ],

  specialist_clinic: [
    // devices
    { perm: AvailableResourceActions.devices.view_page },
    { perm: AvailableResourceActions.devices.view_detail_page },
    { perm: AvailableResourceActions.devices.read_alarms },
    { perm: AvailableResourceActions.devices.read_base },
    { perm: AvailableResourceActions.devices.read_details_section },

    // organizations
    { perm: AvailableResourceActions.organizations.read_base, scope: 'org' },
    {
      perm: AvailableResourceActions.organizations.update_base,
      scope: 'org',
      only_org_admin: true,
    },
    // visualization
    { perm: AvailableResourceActions.visualization.view_rom_labels, scope: 'org' },
    { perm: AvailableResourceActions.visualization.view_patient_history, scope: 'org' },
    // ingestion
    { perm: AvailableResourceActions.ingestions.read_base, scope: 'org' },
    { perm: AvailableResourceActions.ingestions.read_section },
    { perm: AvailableResourceActions.ingestions.read_file_names, scope: 'org' },
    {
      perm: AvailableResourceActions.ingestions.read_processed_json,
      scope: 'org',
    },
    { perm: AvailableResourceActions.ingestions.upload_zips, scope: 'org' },
    { perm: AvailableResourceActions.ingestions.download_zips, scope: 'org' },

    // sessions
    { perm: AvailableResourceActions.sessions.read_section },
    { perm: AvailableResourceActions.sessions.read_base, scope: 'org' },
    { perm: AvailableResourceActions.sessions.read_main_panel, scope: 'all' },

    // patients
    { perm: AvailableResourceActions.patients.read_base },
    { perm: AvailableResourceActions.patients.create },
    { perm: AvailableResourceActions.patients.delete },
    { perm: AvailableResourceActions.patients.update_base },
    { perm: AvailableResourceActions.patients.update_personal_data },
    { perm: AvailableResourceActions.patients.update_config },
    { perm: AvailableResourceActions.patients.update_add_to_org },
    { perm: AvailableResourceActions.patients.update_del_from_org },
    { perm: AvailableResourceActions.patients.update_block_status },
    { perm: AvailableResourceActions.patients.update_config_history },


    // dashboards
    { perm: 'dashboards:*' },

    // users
    { perm: AvailableResourceActions.users.read_base, scope: 'org' },
    { perm: AvailableResourceActions.users.read_section },
    {
      perm: AvailableResourceActions.users.update_is_admin,
      scope: 'org',
      only_org_admin: true,
    },
    {
      perm: AvailableResourceActions.users.update_active,
      scope: 'org',
      only_org_admin: true,
    },

    // incidences
    { perm: AvailableResourceActions.incidences.read_section },

    // surveys
    { perm: AvailableResourceActions.surveys.read_section },
    { perm: AvailableResourceActions.surveys.read_pending_section },

    // visualization
    {
      perm: AvailableResourceActions.visualization.view_steps_ranking_graphs,
      scope: 'org',
    },
    {
      perm: AvailableResourceActions.visualization.view_assistance_modes_graphs,
      scope: 'org',
    },
    // videos
    { perm: AvailableResourceActions.videos.read, scope: 'all' },
    { perm: AvailableResourceActions.videos.manage_tags, scope: 'all' },
  ],
};

// =================== FUNCTIONS ========================

export function getRulesForRole(role: Role): Rule[] {
  return ROLE_RULES[role] ?? [];
}

export function can(role: Role, perm: Permission, is_admin?: boolean): boolean {
  const rules = getRulesForRole(role);
  const [resource] = perm.split(':') as [Resource];

  return rules.some((r) => {
    if (r.only_org_admin && !is_admin) return false;

    return r.perm === perm || r.perm === '*' || r.perm === `${resource}:*`;
  });
}

export function getScopeFor(role: Role, perm: Permission): Scope | null {
  const rules = getRulesForRole(role);
  const [resource] = perm.split(':') as [Resource];

  const rule =
    rules.find((r) => r.perm === perm) ??
    rules.find((r) => r.perm === `${resource}:*`) ??
    rules.find((r) => r.perm === '*');

  return rule?.scope ?? null;
}
